import { useContext } from 'react';
import { WrappedFieldProps } from 'redux-form';
import Input from 'shared/components/Input';
import Markdown from 'shared/components/Markdown';
import MaterialInput from 'shared/components/MaterialInput';
import { COLORS, FONT_SIZES, SPACINGS } from 'shared/styles/variables';
import styled, { ThemeContext } from 'styled-components';

import { InputStyles } from 'lib/interfaces/input-styles';

const OutlinedContainer = styled('div')`
  margin-bottom: ${SPACINGS.base * 2}px;
`;

const MaterialContainer = styled('div')`
  margin-bottom: ${SPACINGS.base}px;

  // NOTE this is needed for material inputs
  position: relative;
  padding-top: ${SPACINGS.base * 2}px;
`;

export const FormError = styled('div')<{ props?: WrappedFieldProps & ThemedFormInputProps }>`
  color: ${(props) => props?.theme?.colorError || COLORS.red100};
  font-size: ${FONT_SIZES.small};
  margin-top: ${SPACINGS.base}px;
`;

export interface ThemedFormInputProps {
  autoFocus?: boolean;
  id: string;
  label?: string;
  className?: string;
  placeholder?: string;
  'data-test'?: string;
}

const ThemedFormInput = (props: WrappedFieldProps & ThemedFormInputProps) => {
  const {
    className,
    input,
    meta: { error, submitFailed },
    autoFocus,
    label,
    placeholder,
    id,
    'data-test': dataTest = 'form',
    ...passthroughProps
  } = props;

  const { inputStyle } = useContext(ThemeContext);

  const hasError = !!(error && submitFailed);

  switch (inputStyle) {
    case InputStyles.Material:
      return (
        <MaterialContainer className={className}>
          {/* Note we fall back to the place holder for material if there is no label */}
          <MaterialInput
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            labelText={label || placeholder}
            {...input}
            {...passthroughProps}
            autoFocus={autoFocus}
            hasError={hasError}
            aria-invalid={hasError ? 'true' : undefined}
            id={id}
            data-test={dataTest}
          />
          <FormError data-test={`${dataTest}-error`} role='alert' props={props}>
            {hasError && error}
          </FormError>
        </MaterialContainer>
      );
    case InputStyles.Outlined:
    default:
      return (
        <OutlinedContainer className={className}>
          {label && (
            <Markdown<'label'> wrapper='label' wrapperProps={{ htmlFor: id }}>
              {label}
            </Markdown>
          )}
          <Input
            {...input}
            {...passthroughProps}
            autoFocus={autoFocus}
            hasError={hasError}
            aria-invalid={hasError ? 'true' : undefined}
            id={id}
            placeholder={placeholder}
            data-test={dataTest}
          />
          <FormError data-test={`${dataTest}-error`} role='alert' props={props}>
            {hasError && error}
          </FormError>
        </OutlinedContainer>
      );
  }
};

export default ThemedFormInput;
